/**
 * Full response store — the read-only assembled document (spec 29 §8).
 *
 * Holds one artifact's document at a time; the reader page loads on mount and
 * resets on unmount. Status, owner and review data come from the section
 * assignments store, not from here.
 */

import { create } from 'zustand'
import { getArtifactDocument, type FullResponseDocument } from '@/lib/api'

interface FullResponseState {
  document: FullResponseDocument | null
  artifactId: string | null
  isLoading: boolean
  error: string | null

  load: (artifactId: string) => Promise<void>
  reset: () => void
}

const initialState = {
  document: null,
  artifactId: null,
  isLoading: false,
  error: null,
}

export const useFullResponseStore = create<FullResponseState>((set, get) => ({
  ...initialState,

  load: async (artifactId) => {
    set({ artifactId, isLoading: true, error: null })
    try {
      const document = await getArtifactDocument(artifactId)
      // A later load for another artifact wins.
      if (get().artifactId !== artifactId) return
      set({ document, isLoading: false })
    } catch (err) {
      if (get().artifactId !== artifactId) return
      set({
        document: null,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load response',
      })
    }
  },

  reset: () => set({ ...initialState }),
}))
